import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { toastError, toastSuccess } from "@/lib/toast";
import { ResponsiveDialogContent } from "@/components/ResponsiveDialogContent";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { DatePickerField } from "@/components/DatePickerField";

type LoanLite = {
  id: number;
  loan_number?: string | number;
  fname?: string;
  sname?: string;
  phone_number?: string;
  BRANCH?: string;
  PRODUCT?: string;
  co_status?: number | string | null;
  loan_status?: number | string | null;
};

const STATUS_OPTIONS = [
  { value: "1", label: "Disburse" },
  { value: "4", label: "Reject" },
];

const DISBURSE_OPTIONS = [
  { value: "1", label: "SendCash" },
  { value: "2", label: "SendManually" },
];

export function LoanDisbursementDialog({
  open,
  onOpenChange,
  loan,
  endpoint,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  loan: LoanLite | null;
  endpoint: (loanId: string) => string;
  onSuccess?: () => void;
}) {
  const initial = useMemo(() => {
    return {
      status: "1",
      sendCash: "1",
     
      disbursement_date: new Date().toLocaleDateString("en-CA"),
    };
  }, [loan]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const isDisburse = form.status === "1";

  const canSubmit = useMemo(() => {
    if (form.status !== "1" && form.status !== "4") return false;
    if (isDisburse && !form.disbursement_date) return false;
    return true;
  }, [form.status, form.disbursement_date, isDisburse]);

  async function submit() {
    if (!loan?.id) {
      toastError("No loan selected");
      return;
    }
    if (!canSubmit || saving) return;

    setSaving(true);
    try {
      const body: Record<string, any> = {
        status: Number(form.status),
       
      };

      if (isDisburse) {
        body.sendCash = Number(form.sendCash);
        body.date = form.disbursement_date;
      }

      const res = await apiFetch<any>(endpoint(String(loan.id)), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res?.ok === false) {
        toastError(res?.message ?? "Failed");
        return;
      }

      toastSuccess(res?.message ?? "Loan processed successfully!");
      onOpenChange(false);
      onSuccess?.();
    } catch (e: any) {
      toastError(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  }

  const titleLine = loan
    ? `Loan #${loan.loan_number ?? loan.id} • ${loan.fname ?? ""} ${loan.sname ?? ""}`.trim()
    : "Loan Disbursement";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent>
        <div className="border-b border-border bg-muted/30 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="text-xl">Pending Loan Disbursement</DialogTitle>
            <div className="text-sm text-muted-foreground mt-1">{titleLine}</div>
          </DialogHeader>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <Label className="text-sm font-medium">
              Status <span className="text-destructive">*</span>
            </Label>
            <div className="mt-2">
              <Select
                value={form.status}
                onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}
                disabled={saving}
              >
                <SelectTrigger className={cn(saving && "opacity-70")}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isDisburse && (
            <>
              <div>
                <Label className="text-sm font-medium">
                  Disbursement Date <span className="text-destructive">*</span>
                </Label>
                <div className="mt-2">
                  <DatePickerField
                    value={form.disbursement_date}
                    onChange={(v) => setForm((p) => ({ ...p, disbursement_date: v }))}
                    placeholder="Disbursement date"
                    min={new Date()}
                    defaultMonth={new Date()}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Send Cash <span className="text-destructive">*</span>
                </Label>
                <div className="mt-2">
                  <Select
                    value={form.sendCash}
                    onValueChange={(v) => setForm((p) => ({ ...p, sendCash: v }))}
                    disabled={saving}
                  >
                    <SelectTrigger className={cn(saving && "opacity-70")}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISBURSE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

         
        </div>

        <div className="border-t border-border px-5 py-4">
          <DialogFooter className="gap-2 sm:gap-3">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)} disabled={saving}>
              Close
            </Button>
            <Button type="button" onClick={submit} disabled={!canSubmit || saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Process
            </Button>
          </DialogFooter>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}